'use client';

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useSession } from 'next-auth/react';
import { SubscriptionTier } from '@prisma/client';
import { Session } from 'next-auth';
import type { RecoveryOffer } from '@/lib/recovery-offers';

interface SessionWithId extends Session {
  user: {
    id: string;
    name?: string | null;
    email?: string | null;
    image?: string | null;
  };
}

interface UsageContextType {
  messagesRemaining: number;
  messageLimit: number;
  canSendMessage: boolean;
  canRequestCreditExtension: boolean;
  canUploadFile: boolean;
  currentTier: SubscriptionTier;
  isSubscriptionExpired: boolean;
  isTrialActive: boolean;
  trialDaysRemaining: number | null;
  trialEndsAt: string | null;
  trialExpiredAt: string | null;
  trialExpiredRecently: boolean;
  upgradeGraceMessagesRemaining: number;
  recoveryOffer: RecoveryOffer | null;
  checkAndUpdateLimits: () => Promise<boolean>;
  incrementMessageCount: () => Promise<void>;
  incrementFileCount: () => Promise<void>;
}

const UsageContext = createContext<UsageContextType | undefined>(undefined);

export function UsageProvider({ children }: { children: React.ReactNode }) {
  const { data: sessionData } = useSession();
  const session = sessionData as SessionWithId | null;

  const [messagesRemaining, setMessagesRemaining] = useState<number>(-1);
  const [messageLimit, setMessageLimit] = useState<number>(-1);
  const [canSendMessage, setCanSendMessage] = useState<boolean>(true);
  const [canRequestCreditExtension, setCanRequestCreditExtension] = useState(false);
  const [canUploadFile, setCanUploadFile] = useState<boolean>(true);
  const [currentTier, setCurrentTier] = useState<SubscriptionTier>('HOBBY');
  const [isSubscriptionExpired, setIsSubscriptionExpired] = useState<boolean>(false);
  const [isTrialActive, setIsTrialActive] = useState<boolean>(false);
  const [trialDaysRemaining, setTrialDaysRemaining] = useState<number | null>(null);
  const [trialEndsAt, setTrialEndsAt] = useState<string | null>(null);
  const [trialExpiredAt, setTrialExpiredAt] = useState<string | null>(null);
  const [trialExpiredRecently, setTrialExpiredRecently] = useState<boolean>(false);
  const [upgradeGraceMessagesRemaining, setUpgradeGraceMessagesRemaining] = useState<number>(0);
  const [recoveryOffer, setRecoveryOffer] = useState<RecoveryOffer | null>(null);

  const checkAndUpdateLimits = useCallback(async () => {
    if (!session?.user?.id) return false;

    try {
      const response = await fetch('/api/usage/check-limits');
      if (!response.ok) throw new Error('Failed to check usage limits');

      const data = await response.json();

      setMessagesRemaining(data.messagesRemaining);
      setMessageLimit(data.messageLimit);
      setCanSendMessage(data.canSendMessage);
      setCanRequestCreditExtension(data.canRequestCreditExtension === true);
      setCanUploadFile(data.canUploadFile);
      setCurrentTier(data.currentTier);
      setIsSubscriptionExpired(data.subscriptionExpired);
      setIsTrialActive(Boolean(data.isTrialActive));
      setTrialDaysRemaining(typeof data.trialDaysRemaining === 'number' ? data.trialDaysRemaining : null);
      setTrialEndsAt(typeof data.trialEndsAt === 'string' ? data.trialEndsAt : null);
      setTrialExpiredAt(typeof data.trialExpiredAt === 'string' ? data.trialExpiredAt : null);
      setTrialExpiredRecently(Boolean(data.trialExpiredRecently));
      setUpgradeGraceMessagesRemaining(
        typeof data.upgradeGraceMessagesRemaining === 'number' ? data.upgradeGraceMessagesRemaining : 0
      );
      setRecoveryOffer(data.recoveryOffer ?? null);

      return data.canSendMessage;
    } catch (error) {
      console.error('Error checking limits:', error);
      return false;
    }
  }, [session?.user?.id]);

  const incrementMessageCount = useCallback(async () => {
    if (!session?.user?.id) return;

    try {
      await fetch('/api/usage/check-limits', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: 'message' }),
      });
      await checkAndUpdateLimits();
    } catch (error) {
      console.error('Error incrementing message count:', error);
    }
  }, [session?.user?.id, checkAndUpdateLimits]);

  const incrementFileCount = useCallback(async () => {
    if (!session?.user?.id) return;

    try {
      await fetch('/api/usage/check-limits', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: 'file' }),
      });
      await checkAndUpdateLimits();
    } catch (error) {
      console.error('Error incrementing file count:', error);
    }
  }, [session?.user?.id, checkAndUpdateLimits]);

  useEffect(() => {
    if (session?.user?.id) {
      checkAndUpdateLimits();
    }
  }, [session?.user?.id, checkAndUpdateLimits]);

  return (
    <UsageContext.Provider
      value={{
        messagesRemaining,
        messageLimit,
        canSendMessage,
        canRequestCreditExtension,
        canUploadFile,
        currentTier,
        isSubscriptionExpired,
        isTrialActive,
        trialDaysRemaining,
        trialEndsAt,
        trialExpiredAt,
        trialExpiredRecently,
        upgradeGraceMessagesRemaining,
        recoveryOffer,
        checkAndUpdateLimits,
        incrementMessageCount,
        incrementFileCount,
      }}>
      {children}
    </UsageContext.Provider>
  );
}

export function useUsage() {
  const context = useContext(UsageContext);
  if (context === undefined) {
    throw new Error('useUsage must be used within a UsageProvider');
  }
  return context;
}
